import { useState } from 'react';
// Animation keyframes
import { keyframes } from '@emotion/react';

import {
  Box,
  Link,
  List,
  Collapse,
  IconButton,
  Typography,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  CircularProgress,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { Iconify } from 'src/components/iconify';
import { SectionEmpty } from 'src/components/page-state';

import { expandToFolder } from './report-tree-utils';
import { useFavoriteToggle } from '../hooks/use-favorite-toggle';
import { ReportOpenInNewTabButton } from './report-open-new-tab-button';

// =====================================================================

// Animation definitions
const starPulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
`;

const slideInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export function ReportTree({
  reports,
  favorites,
  selectedFolder,
  expandedFolders: controlledExpandedFolders,
  activeReportUri,
  onToggleFavorite,
  onFolderSelect,
  onReportClick,
}) {
  const router = useRouter();
  const [internalExpandedFolders, setInternalExpandedFolders] = useState({});
  const isControlled = controlledExpandedFolders !== undefined;
  const expandedFolders = isControlled ? controlledExpandedFolders : internalExpandedFolders;
  const { loadingFavorites, animatingFavorites, handleToggleFavorite } = useFavoriteToggle(onToggleFavorite);

  const toggleFolder = (path) => {
    if (!isControlled) {
      // Expand only — selecting a folder should not collapse open folders.
      setInternalExpandedFolders((prev) => expandToFolder(prev, path));
    }

    if (onFolderSelect) {
      onFolderSelect(path);
    }
  };

  const handleReportClick = (uri, resourceType = 'reportUnit') => {
    if (onReportClick) {
      onReportClick(uri, resourceType);
      return;
    }

    router.push(`${paths.reports.view(uri)}?type=${resourceType}`);
  };

  const renderFolder = (folder, level = 0) => {
    const isExpanded = expandedFolders[folder.path] ?? folder.expanded;
    const hasChildren = folder.children && folder.children.length > 0;

    return (
      <Box key={folder.path}>
        {/* Folder header */}
        <ListItemButton
          onClick={() => toggleFolder(folder.path)}
          sx={{
            pl: 3 + level * 2,
            bgcolor: selectedFolder?.path === folder.path ? 'primary.lighter' : 'transparent',
            fontWeight: level === 0 ? 'bold' : 'normal',
            '&:hover': {
              bgcolor: selectedFolder?.path === folder.path ? 'primary.lighter' : 'action.hover',
            },
          }}
        >
          {hasChildren ? (
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Iconify
                icon={isExpanded ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
                width={20}
              />
            </ListItemIcon>
          ) : (
            <Box sx={{ width: 32 }} />
          )}
          <ListItemText primary={folder.name} />
        </ListItemButton>

        {/* Expanded content */}
        <Collapse in={isExpanded} timeout="auto">
          <List component="div" disablePadding>
            {hasChildren && folder.children.map((child) => renderFolder(child, level + 1))}
          </List>
        </Collapse>
      </Box>
    );
  };

  const isFavoriteReport = (report) => favorites.has(report.uri);

  const allReports = [];
  const flattenReports = (folders) => {
    folders.forEach((folder) => {
      if (folder.reports) {
        allReports.push(...folder.reports);
      }
      if (folder.children) {
        flattenReports(folder.children);
      }
    });
  };
  flattenReports(reports);

  const favoriteReports = allReports.filter(isFavoriteReport);
  const MAX_FAVORITES_VISIBLE = 5;
  const FAVORITE_ITEM_HEIGHT = 40;
  const BASE_VIEWPORT_HEIGHT = 57;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {favoriteReports.length > 0 && (
        <Box
          sx={{
            animation: `${slideInDown} 0.3s ease-in-out`,
          }}
        >
          <Typography variant="subtitle2" sx={{ p: 2, pt: 1, pb: 1, fontWeight: 'bold', color: 'warning.main', m: 0 }}>
            ⭐ Favorites
          </Typography>
          <Box
            sx={{
              flex: '0 0 auto',
              maxHeight: '180px',
              overflow: 'auto',
              borderBottom: '2px solid',
              borderColor: 'divider',
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <List disablePadding sx={{ pb: 1 }}>
              {favoriteReports.map((report) => (
                <ListItemButton
                  key={report.uri}
                  sx={{
                    pl: 2,
                    py: 0.5,
                    minHeight: '35px',
                    transition: 'all 0.2s ease-in-out',
                    animation: animatingFavorites.has(report.uri) ? `${slideInDown} 0.3s ease-in-out` : 'none',
                    bgcolor: activeReportUri === report.uri ? 'primary.lighter' : 'transparent',
                    '&:hover': {
                      bgcolor: activeReportUri === report.uri ? 'primary.lighter' : 'action.hover',
                    },
                    '&:disabled': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  disabled={loadingFavorites.has(report.uri)}
                >
                  <ListItemIcon sx={{ minWidth: 25, m: 0, p: 0 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(report.uri);
                      }}
                      disabled={loadingFavorites.has(report.uri)}
                      sx={{
                        p: 0,
                        m: 0,
                        transition: 'all 0.3s ease-in-out',
                        animation: animatingFavorites.has(report.uri)
                          ? `${starPulse} 0.3s ease-in-out`
                          : 'none',
                        '&:hover': {
                          transform: loadingFavorites.has(report.uri) ? 'none' : 'scale(1.2)',
                        },
                        '&:disabled': {
                          opacity: 1,
                        },
                      }}
                    >
                      {loadingFavorites.has(report.uri) ? (
                        <CircularProgress size={20} sx={{ color: 'warning.main' }} />
                      ) : (
                        <Iconify
                          icon="eva:star-fill"
                          width={20}
                          sx={{ color: 'warning.main', m: 0 }}
                        />
                      )}
                    </IconButton>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Link
                          component="span"
                          onClick={() => handleReportClick(report.uri, report.resourceType || 'reportUnit')}
                          sx={{
                            cursor: 'pointer',
                            color: activeReportUri === report.uri ? 'primary.dark' : 'primary.main',
                            fontWeight: activeReportUri === report.uri ? 600 : 400,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {report.label}
                        </Link>
                        <ReportOpenInNewTabButton
                          uri={report.uri}
                          resourceType={report.resourceType || 'reportUnit'}
                        />
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Box>
      )}

      {reports.length > 0 && favoriteReports.length > 0 && (
        <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 'bold', m: 0 }}>
          All Reports
        </Typography>
      )}

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          maxHeight: favoriteReports.length > 0
            ? `calc(${BASE_VIEWPORT_HEIGHT}vh - ${Math.min(favoriteReports.length, MAX_FAVORITES_VISIBLE) * FAVORITE_ITEM_HEIGHT}px)`
            : '68vh',
          pr: 1,
        }}
      >
        {reports.length > 0 && (
          <List disablePadding>
            {reports.map((folder) => renderFolder(folder))}
          </List>
        )}

        {reports.length === 0 && (
          <SectionEmpty
            title="No reports found"
            description="Try a different search or pick another folder."
          />
        )}
      </Box>
    </Box>
  );
}
